import React, { useState, useEffect, useCallback } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import { 
  Users, UserPlus, ArrowLeft, Save, 
  Stethoscope, Activity, ClipboardList, CheckCircle, FileText, X,
  Circle, ChevronRight, Phone, User, AlertCircle, Heart, Thermometer, Wind, Eye,
  Edit2
} from 'lucide-react';

import './FitnessCertificate.css';
import { safeQuery } from '../utils/dbUtils';

const FITNESS_PURPOSES = [
  'Swimming / Pool Fitness Clearance',
  'Employment',
  'School/College Admission',
  'Sports/Gym Membership',
  'Insurance Underwriting',
  'Driving License (General)',
  'Fit to Fly/Travel',
  'Hostel/Accommodation',
  'Pre-operative Clearance',
  'Other'
];

const PREDEFINED_FINDINGS = [
  "No history of epilepsy, syncope, severe cardiac or respiratory illness that may pose risk during swimming.",
  "No medical contraindication found for swimming activity."
];

function FitnessCertificate({ existingPatient = null, appointmentId = null, individualId = null, doctor, clinic, onClose, onSuccess }) {
  // Helper for lazy initialization
  const getInitialDraft = () => {
    if (existingPatient) return null;
    try {
      const stash = localStorage.getItem('klinik_cert_stash');
      return stash ? JSON.parse(stash) : null;
    } catch (e) { return null; }
  };

  const initialDraft = getInitialDraft();

  const [step, setStep] = useState(() => {
    if (initialDraft && initialDraft.step) return initialDraft.step;
    return existingPatient ? 2 : 1;
  });

  const [loading, setLoading] = useState(false);
  
  const [phoneNumber, setPhoneNumber] = useState(() => {
    if (initialDraft && initialDraft.phoneNumber) return initialDraft.phoneNumber;
    return existingPatient?.phone || '';
  });

  const [patientData, setPatientData] = useState(() => {
    if (initialDraft && initialDraft.patientData) return initialDraft.patientData;
    return existingPatient;
  });
  
  // Section Expansion States
  const [isVitalsExpanded, setIsVitalsExpanded] = useState(true);
  const [isExamExpanded, setIsExamExpanded] = useState(true);
  const [isFindingsExpanded, setIsFindingsExpanded] = useState(true);
  const [isEditingVerdict, setIsEditingVerdict] = useState(false);

  // Form State
  const [formData, setFormData] = useState(() => {
    if (initialDraft && initialDraft.formData) return initialDraft.formData;
    return {
      patient_name: existingPatient ? existingPatient.name : '',
      patient_age: existingPatient ? existingPatient.age : '',
      patient_gender: existingPatient ? existingPatient.gender || 'Male' : 'Male',
      purpose: 'Swimming / Pool Fitness Clearance',
      custom_purpose: '',
      blood_pressure: '',
      pulse: '',
      temperature: '',
      spo2: '',
      cvs: 'Normal',
      rs: 'Normal',
      cns: 'Normal',
      skin: 'No contagious/infectious disease detected',
      general_exam: 'Conscious, oriented, afebrile',
      selected_findings: PREDEFINED_FINDINGS,
      verdict: 'Fit',
      custom_verdict: '',
      restrictions: ''
    };
  });


  // B. Atomic Save Logic
  const saveDraft = useCallback(() => {
    if (existingPatient) return;

    const draft = {
      formData, phoneNumber, patientData, step,
      lastSaved: Date.now()
    };
    
    // Check if there is actual content
    const hasContent = formData.blood_pressure || formData.temperature || formData.custom_purpose || (formData.selected_findings && formData.selected_findings.length > 2);
    if (hasContent || patientData) {
      localStorage.setItem('klinik_cert_stash', JSON.stringify(draft));
    }
  }, [formData, phoneNumber, patientData, step, existingPatient]);

  // C. Persistence Hook - Save changes to localStorage (Throttled/Reactive)
  useEffect(() => {
    saveDraft();
  }, [saveDraft]);

  // D. Terminal Recovery Listener - Synchronous Flush
  useEffect(() => {
    const handlePreReload = () => {
      console.log('[Atomic Save] Terminal recovery detected. Flushing Cert to disk...');
      saveDraft();
    };
    window.addEventListener('klinik-pre-reload-save', handlePreReload);
    return () => window.removeEventListener('klinik-pre-reload-save', handlePreReload);
  }, [saveDraft]);




  const handlePhoneSubmit = async (e) => {
    if (e) e.preventDefault();
    if (phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('phone', phoneNumber)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPatientData(data);
        setFormData(prev => ({
          ...prev,
          patient_name: data.name,
          patient_age: data.age,
          patient_gender: data.gender || 'Male'
        }));
        toast.success('Patient found');
      } else {
        setPatientData(null);
        toast.info('New patient record will be created');
      }
      setStep(2);
    } catch (err) {
      console.error('Error fetching patient:', err);
      toast.error('Error searching for patient');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getDefaultVerdictText = (verdict, purpose, customPurpose) => {
    const dispPurpose = purpose === 'Other' ? customPurpose : purpose;
    if (verdict === 'Fit') {
      const purposeSuffix = dispPurpose?.toLowerCase().includes('swimming') 
        ? ' swimming and pool-related activities.' 
        : ` ${dispPurpose?.toLowerCase().replace('clearance', '').trim() || 'the activities mentioned above'}.`;
      return `The above-mentioned individual is medically fit to participate in${purposeSuffix}`;
    } else {
      const purposeSuffix = dispPurpose?.toLowerCase().replace('clearance', '').trim() || 'the specified purpose';
      return `The above-mentioned individual is medically unfit for ${purposeSuffix}.`;
    }
  };

  const toggleVerdictEdit = () => {
    if (!isEditingVerdict) {
      // Pre-fill with default text if opening edit mode
      const defaultText = getDefaultVerdictText(formData.verdict, formData.purpose, formData.custom_purpose);
      setFormData(prev => ({ ...prev, custom_verdict: defaultText }));
    }
    setIsEditingVerdict(!isEditingVerdict);
  };

  const updateVerdictStatus = (status) => {
    const defaultText = getDefaultVerdictText(status, formData.purpose, formData.custom_purpose);
    setFormData(prev => ({ 
      ...prev, 
      verdict: status,
      // Also update custom text if it hasn't been modified yet or if we're not in edit mode
      custom_verdict: prev.custom_verdict === getDefaultVerdictText(prev.verdict, prev.purpose, prev.custom_purpose) || !prev.custom_verdict
        ? defaultText 
        : prev.custom_verdict
    }));
  };

  const toggleFinding = (finding) => {
    setFormData(prev => {
      const isSelected = prev.selected_findings.includes(finding);
      if (isSelected) {
        return { ...prev, selected_findings: prev.selected_findings.filter(f => f !== finding) };
      } else {
        return { ...prev, selected_findings: [...prev.selected_findings, finding] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_name || !formData.patient_age) {
      toast.error('Patient name and age are required');
      return;
    }

    setLoading(true);
    try {
      let currentPatientId = patientData?.id;

      // Create new patient if doesn't exist
      if (!currentPatientId) {
        const result = await safeQuery(
          supabase
            .from('patients')
            .insert([{
              name: formData.patient_name,
              phone: phoneNumber,
              age: formData.patient_age,
              gender: formData.patient_gender,
              clinic_id: clinic.id
            }])
            .select()
            .single(),
          { label: 'Create New Patient' }
        );
        
        if (result.error) throw result.error;
        currentPatientId = result.data.id;
      } else if (
        patientData.name !== formData.patient_name || 
        String(patientData.age) !== String(formData.patient_age) || 
        patientData.gender !== formData.patient_gender
      ) {
        // Update the underlying patient record if the doctor corrected their information
        await safeQuery(
          supabase.from('patients').update({
             name: formData.patient_name,
             age: formData.patient_age,
             gender: formData.patient_gender
          }).eq('id', currentPatientId),
          { label: 'Update Patient Info' }
        );
      }

      const payload = {
        doctor_id: doctor.id,
        clinic_id: clinic.id,
        appointment_id: appointmentId,
        patient_id: currentPatientId,


        patient_name: formData.patient_name,
        patient_age: formData.patient_age,
        patient_gender: formData.patient_gender,
        patient_phone: phoneNumber,
        purpose: formData.purpose === 'Other' ? formData.custom_purpose : formData.purpose,
        blood_pressure: formData.blood_pressure,
        pulse: formData.pulse,
        temperature: formData.temperature,
        spo2: formData.spo2,
        cvs: formData.cvs,
        rs: formData.rs,
        cns: formData.cns,
        skin: formData.skin,
        general_exam: formData.general_exam,
        selected_findings: formData.selected_findings,
        verdict: isEditingVerdict ? formData.custom_verdict : formData.verdict,
        restrictions: formData.restrictions,
        certificate_type: 'fitness'
      };


      const result = await safeQuery(
        supabase
          .from('certificates')
          .insert([payload])
          .select()
          .single(),
        { label: 'Issue Certificate' }
      );

      if (result.error) throw result.error;

      toast.success('Fitness Certificate issued successfully!');
      
      // Successfully saved - clear the draft
      localStorage.removeItem('klinik_cert_stash');
      
      onSuccess(result.data);
    } catch (error) {
      console.error('Error issuing certificate:', error);
      // safeQuery toasts the error automatically
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fitness-cert-overlay">
      <motion.div 
        className="fitness-cert-modal"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
      >
        <div className="modal-header">
          <div className="title-area">
            {step === 2 && (
              <button className="back-btn" onClick={() => setStep(1)}>
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h2>Issue Fitness Certificate</h2>
              <p className="subtitle">Clinical evaluation and clearance</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="step-indicator">
              <span className={`dot ${step >= 1 ? 'active' : ''}`}></span>
              <span className={`line ${step >= 2 ? 'active' : ''}`}></span>
              <span className={`dot ${step >= 2 ? 'active' : ''}`}></span>
            </div>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="phone-step-container"
              >
                <div className="phone-input-section">
                  <div className="icon-circle">
                    <Phone size={32} />
                  </div>
                  <h3>Enter Patient Phone Number</h3>
                  <p>Start by identifying the patient using their mobile number</p>
                  
                  <form onSubmit={handlePhoneSubmit} className="phone-form">
                    <div className="phone-input-group">
                      <Phone className="input-icon" size={20} />
                      <input 
                        type="tel" 
                        placeholder="10-digit mobile number" 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        autoFocus
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="continue-btn"
                      disabled={loading || phoneNumber.length < 10}
                    >
                      {loading ? 'Searching...' : 'Continue'}
                      <ChevronRight size={18} />
                    </button>
                  </form>
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="form-step-container"
              >
                <form id="fitnessForm" onSubmit={handleSubmit}>
                  {/* Patient Info Card */}
                  <div className="info-card patient-card">
                    <div className="card-header">
                      <div className="label"><Users size={16} /> Patient Identification</div>
                      {patientData && <span className="existing-badge">Existing Patient</span>}
                    </div>
                    <div className="card-body grid-3">
                      <div className="input-field">
                        <label>Full Name</label>
                        <User className="field-icon" size={14} />
                        <input 
                          type="text" 
                          name="patient_name"
                          value={formData.patient_name}
                          onChange={handleInputChange}
                          placeholder="Full Name"
                          required
                        />
                      </div>
                      <div className="input-field">
                        <label>Age</label>
                        <Activity className="field-icon" size={14} />
                        <input 
                          type="text"
                          inputMode="decimal"
                          name="patient_age"
                          value={formData.patient_age}
                          onChange={handleInputChange}
                          placeholder="Age (e.g. 2.5)"
                          required
                        />
                      </div>
                      <div className="input-field">
                        <label>Gender</label>
                        <User className="field-icon" size={14} />
                        <select 
                          name="patient_gender"
                          value={formData.patient_gender}
                          onChange={handleInputChange}
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Purpose Selection */}
                  <div className="info-card">
                    <div className="card-header">
                      <div className="label"><ClipboardList size={16} /> Certificate Purpose</div>
                    </div>
                    <div className="card-body">
                      <select name="purpose" value={formData.purpose} onChange={handleInputChange} className="purpose-select">
                        {FITNESS_PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      {formData.purpose === 'Other' && (
                        <div className="input-field mt-3">
                          <label>Specify Purpose</label>
                          <input 
                            type="text" 
                            name="custom_purpose"
                            value={formData.custom_purpose} 
                            onChange={handleInputChange}
                            placeholder="Reason for certificate"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vitals Section */}
                  <div className={`info-card collapsible ${isVitalsExpanded ? 'expanded' : ''}`}>
                    <div className="card-header clickable" onClick={() => setIsVitalsExpanded(!isVitalsExpanded)}>
                      <div className="label"><Activity size={16} /> Physical Vitals</div>
                      <ChevronRight className="toggle-icon" size={16} />
                    </div>
                    <AnimatePresence>
                      {isVitalsExpanded && (
                        <motion.div 
                          className="card-body grid-4"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <div className="input-field">
                            <label>BP (mmHg)</label>
                            <Heart className="field-icon" size={14} />
                            <input 
                              type="text" 
                              name="blood_pressure" 
                              value={formData.blood_pressure} 
                              onChange={handleInputChange} 
                              placeholder="120/80" 
                            />
                          </div>
                          <div className="input-field">
                            <label>Pulse (bpm)</label>
                            <Activity className="field-icon" size={14} />
                            <input 
                              type="text" 
                              inputMode="numeric"
                              name="pulse" 
                              value={formData.pulse} 
                              onChange={handleInputChange} 
                              placeholder="72" 
                            />
                          </div>
                          <div className="input-field">
                            <label>Temp</label>
                            <Thermometer className="field-icon" size={14} />

                            <input 
                              type="text" 
                              name="temperature" 
                              value={formData.temperature} 
                              onChange={handleInputChange} 
                              placeholder="98.6" 
                            />
                          </div>

                          <div className="input-field">
                            <label>SpO2 (%)</label>
                            <Wind className="field-icon" size={14} />
                            <input 
                              type="text" 
                              inputMode="numeric"
                              name="spo2" 
                              value={formData.spo2} 
                              onChange={handleInputChange} 
                              placeholder="98" 
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Clinical Exam Section */}
                  <div className={`info-card collapsible ${isExamExpanded ? 'expanded' : ''}`}>
                    <div className="card-header clickable" onClick={() => setIsExamExpanded(!isExamExpanded)}>
                      <div className="label"><Stethoscope size={16} /> Clinical Examination</div>
                      <ChevronRight className="toggle-icon" size={16} />
                    </div>
                    <AnimatePresence>
                      {isExamExpanded && (
                        <motion.div 
                          className="card-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <div className="input-field full-width mb-4">
                            <label>General Examination</label>
                            <Eye className="field-icon" size={14} />
                            <input type="text" name="general_exam" value={formData.general_exam} onChange={handleInputChange} placeholder="General status" />
                          </div>
                          <div className="grid-2">
                            <div className="input-field">
                              <label>CVS</label>
                              <Heart className="field-icon" size={14} />
                              <input type="text" name="cvs" value={formData.cvs} onChange={handleInputChange} />
                            </div>
                            <div className="input-field">
                              <label>Respiratory (RS)</label>
                              <Wind className="field-icon" size={14} />
                              <input type="text" name="rs" value={formData.rs} onChange={handleInputChange} />
                            </div>
                            <div className="input-field">
                              <label>CNS</label>
                              <Activity className="field-icon" size={14} />
                              <input type="text" name="cns" value={formData.cns} onChange={handleInputChange} />
                            </div>
                            <div className="input-field">
                              <label>Skin</label>
                              <AlertCircle className="field-icon" size={14} />
                              <input type="text" name="skin" value={formData.skin} onChange={handleInputChange} />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Findings Checklist */}
                  <div className={`info-card collapsible ${isFindingsExpanded ? 'expanded' : ''}`}>
                    <div className="card-header clickable" onClick={() => setIsFindingsExpanded(!isFindingsExpanded)}>
                      <div className="label"><FileText size={16} /> Clinical Findings</div>
                      <ChevronRight className="toggle-icon" size={16} />
                    </div>
                    <AnimatePresence>
                      {isFindingsExpanded && (
                        <motion.div 
                          className="card-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          <div className="findings-checklist-grid">
                            {PREDEFINED_FINDINGS.map((finding, idx) => (
                              <div 
                                key={idx} 
                                className={`finding-cell ${formData.selected_findings.includes(finding) ? 'selected' : ''}`}
                                onClick={() => toggleFinding(finding)}
                              >
                                {formData.selected_findings.includes(finding) ? <CheckCircle size={18} className="check-on" /> : <Circle size={18} className="check-off" />}
                                <span>{finding}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Verdict Block */}
                  <div className="verdict-block">
                    <div className="verdict-header">
                      <div className="label">Medical Verdict</div>
                      <button 
                        type="button" 
                        className={`edit-verdict-toggle ${isEditingVerdict ? 'active' : ''}`}
                        onClick={toggleVerdictEdit}
                        title="Customize verdict text"
                      >
                        <Edit2 size={14} /> {isEditingVerdict ? 'Use Default' : 'Edit Phrasing'}
                      </button>
                    </div>

                    {!isEditingVerdict ? (
                      <div className="verdict-controls">
                        <button 
                          type="button" 
                          className={`verdict-btn fit ${formData.verdict === 'Fit' ? 'active' : ''}`}
                          onClick={() => updateVerdictStatus('Fit')}
                        >
                          <CheckCircle size={20} /> Medically Fit
                        </button>
                        <button 
                          type="button" 
                          className={`verdict-btn unfit ${formData.verdict === 'Unfit' ? 'active' : ''}`}
                          onClick={() => updateVerdictStatus('Unfit')}
                        >
                          <AlertCircle size={20} /> Medically Unfit
                        </button>
                      </div>

                    ) : (
                      <div className="input-field full-width">
                        <textarea 
                          name="custom_verdict"
                          value={formData.custom_verdict}
                          onChange={handleInputChange}
                          placeholder="Enter custom medical verdict (e.g., Medically fit with corrective lenses)"
                          rows="2"
                          className="custom-verdict-area"
                          autoFocus
                        />
                        <p className="field-hint">This text will appear in the "Verdict" section of the certificate.</p>
                      </div>
                    )}


                    {formData.verdict === 'Unfit' && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }}
                        className="input-field full-width mt-4"
                      >
                        <label>Reason / Restrictions</label>
                        <textarea 
                          name="restrictions"
                          value={formData.restrictions} 
                          onChange={handleInputChange}
                          placeholder="Why is the patient unfit?"
                          rows="3"
                        />
                      </motion.div>
                    )}
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="modal-footer">
          {step === 2 && (
            <div className="action-buttons">
              <button className="cancel-btn" onClick={onClose} disabled={loading}>
                Discard
              </button>
              <button 
                type="submit" 
                form="fitnessForm"
                className="issue-btn" 
                disabled={loading}
              >
                {loading ? 'Processing...' : (
                  <>
                    <Save size={18} /> Issue & Preview
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default FitnessCertificate;
